(function () {
  'use strict';

  /**
   * Bundle Builder
   *
   * Augments the existing product form (buy_button block) so that when the
   * merchant clicks "Add to cart", all bundle component products are added to
   * the cart alongside the main bundle product — each child with parent_id set
   * to the main variant id so they render as nested lines in the side cart.
   *
   * No separate add-to-cart button is rendered; the block only provides the
   * component variant pickers. The existing form submit is intercepted in the
   * capture phase (before custom-async.js's bubble listener) to take over.
   *
   * Liquid emits:
   *   .bundle-builder[data-bundle-section][data-main-variant]
   *   .bundle-item[data-variants][data-product-id]
   *   .bundle-item__img
   *   input[type="radio"][data-bundle-product][data-option-index]
   *   input.bundle-item-variant-id[data-product-id]
   *   .bundle-color-label[data-product][data-option-index]
   */

  /* ------------------------------------------------------------------ */
  /* Image helpers                                                        */
  /* ------------------------------------------------------------------ */

  /**
   * Resize a Shopify CDN image URL to the given size string (e.g. '160x').
   * Strips any existing Shopify size suffix first.
   */
  function resizeShopifyImage(src, size) {
    if (!src) return '';
    var cleaned = src.replace(/_(?:pico|icon|thumb|small|compact|medium|large|grande|original|\d+x\d*|\d*x\d+)\./i, '.');
    return cleaned.replace(/(\.[a-z]+)(\?|$)/i, '_' + size + '$1$2');
  }

  /* ------------------------------------------------------------------ */
  /* Variant matching                                                     */
  /* ------------------------------------------------------------------ */

  function findMatchingVariant(variants, selectedValues) {
    return variants.find(function (v) {
      return v.options.every(function (opt, i) {
        return opt === selectedValues[i];
      });
    });
  }

  /**
   * Read all :checked radios for a bundle item card.
   * Returns an ordered array (one value per option index).
   */
  function getSelectedOptions(card) {
    var productId = card.dataset.productId;
    var values = [];
    card.querySelectorAll('input[type="radio"][data-bundle-product="' + productId + '"]:checked').forEach(function (radio) {
      values[parseInt(radio.dataset.optionIndex, 10)] = radio.value;
    });
    return values;
  }

  /* ------------------------------------------------------------------ */
  /* Per-card update: variant id + image + availability marks            */
  /* ------------------------------------------------------------------ */

  function updateCard(card) {
    var productId = card.dataset.productId;
    var variants;
    try {
      variants = JSON.parse(card.dataset.variants);
    } catch (e) {
      return;
    }

    var selectedValues = getSelectedOptions(card);
    var matched = findMatchingVariant(variants, selectedValues);
    if (!matched) return;

    // Update hidden variant-id input
    var hiddenInput = card.querySelector('input.bundle-item-variant-id[data-product-id="' + productId + '"]');
    if (hiddenInput) hiddenInput.value = matched.id;

    // Swap image to the matched variant's image (if it has one)
    var img = card.querySelector('.bundle-item__img');
    if (img && matched.featured_image && matched.featured_image.src) {
      img.src = resizeShopifyImage(matched.featured_image.src, '160x');
      if (matched.featured_image.alt) img.alt = matched.featured_image.alt;
    }

    // Re-evaluate availability for every radio in the card
    var allRadios = card.querySelectorAll('input[type="radio"][data-bundle-product="' + productId + '"]');
    allRadios.forEach(function (radio) {
      var testValues = selectedValues.slice();
      testValues[parseInt(radio.dataset.optionIndex, 10)] = radio.value;
      var testVariant = findMatchingVariant(variants, testValues);
      var label = card.querySelector('label[for="' + radio.id + '"]');
      if (label) {
        label.classList.toggle('disabled', !!(testVariant && !testVariant.available));
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /* Color label (selected value shown next to option name)              */
  /* ------------------------------------------------------------------ */

  function updateColorLabel(card, optionIndex, value) {
    var productId = card.dataset.productId;
    var label = card.querySelector(
      '.bundle-color-label[data-product="' + productId + '"][data-option-index="' + optionIndex + '"]'
    );
    if (label) label.textContent = value;
  }

  /* ------------------------------------------------------------------ */
  /* Cart add                                                             */
  /* ------------------------------------------------------------------ */

  function addBundleToCart(container, form) {
    // Main bundle product variant comes from the product form
    var mainVariantInput = form.querySelector('input[name="id"], input[name="variant-id"]');
    var mainVariantId = mainVariantInput ? parseInt(mainVariantInput.value, 10) : 0;
    if (!mainVariantId) return;

    // Unique nonce groups all children in this add so the Cart Transform
    // function can link them to the parent it creates server-side.
    var nonce = 'BUNDLE-' + Date.now().toString(36);

    // Collect all form properties (dog name, phone number, name style,
    // charm, tassel, etc.). These go on the MAIN product line only,
    // not on the child lines.
    var formProperties = {};
    form.querySelectorAll('[name^="properties["]').forEach(function (el) {
      if (el.disabled || el.value === '') return;
      if ((el.type === 'radio' || el.type === 'checkbox') && !el.checked) return;
      var key = el.name.slice('properties['.length, -1);
      formProperties[key] = el.value;
    });

    // Discount from data-attributes on the container (set in Liquid).
    var discountType = container.dataset.bundleDiscountType || 'price';
    var discountValue = container.dataset.bundleDiscountValue || '0';

    // Build component name properties for the parent line
    // so they show as visible line item properties in the cart (like Wild Ones).
    var componentProps = {};
    var componentIndex = 1;
    container.querySelectorAll('.bundle-item').forEach(function (card) {
      var titleEl = card.querySelector('.bundle-item__title');
      if (titleEl) {
        componentProps['Item ' + componentIndex] = titleEl.textContent.trim();
        componentIndex++;
      }
    });

    // Only children go into the cart. The CT function creates the parent
    // line server-side using _bundle_parent as the parentVariantId.
    // Customer properties are on the children so they're accessible
    // via item_components in Liquid for display on the CT parent.
    var items = [];
    container.querySelectorAll('input.bundle-item-variant-id').forEach(function (input) {
      var variantId = parseInt(input.value, 10);
      if (!variantId) return;
      items.push({
        id: variantId,
        quantity: 1,
        properties: Object.assign({}, formProperties, {
          _bundle_id: nonce,
          _bundle_parent: String(mainVariantId),
          _bundle_discount_type: discountType,
          _bundle_discount_value: discountValue
        })
      });
    });

    if (!items.length) return;

    form.classList.add('processing', 'loading');

    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        form.classList.remove('processing', 'loading');
        if (data.status && data.status !== 200) {
          throw new Error(data.description || 'Could not add bundle to cart');
        }
        // Open the cart drawer using the theme's ajaxCart module
        if (typeof ajaxCart !== 'undefined' && ajaxCart.load) {
          ajaxCart.load(false, true, false, true);
        } else {
          window.location.href = window.Shopify.routes.root + 'cart';
        }
      })
      .catch(function (err) {
        form.classList.remove('processing', 'loading');
        console.error('[BundleBuilder]', err);
        alert(err.message || 'Something went wrong. Please try again.');
      });
  }

  /* ------------------------------------------------------------------ */
  /* Form intercept                                                       */
  /* ------------------------------------------------------------------ */

  function setupFormIntercept(container) {
    var sectionId = container.dataset.bundleSection;
    // The product form uses id="main-product-form-{sectionId}"
    var form = document.getElementById('main-product-form-' + sectionId)
      || document.querySelector('form.f8pr[data-section-id="' + sectionId + '"]');
    if (!form) return;

    // Use capture phase so we run before custom-async.js's bubble listener.
    // stopImmediatePropagation prevents the theme's own addCartItem from firing.
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      e.stopImmediatePropagation();
      if (form.classList.contains('processing')) return;
      addBundleToCart(container, form);
    }, { capture: true });
  }

  /* ------------------------------------------------------------------ */
  /* Init a single bundle builder container                              */
  /* ------------------------------------------------------------------ */

  function initBundleBuilder(container) {
    if (!container || container.dataset.bundleInitialized) return;
    container.dataset.bundleInitialized = '1';

    // Listen for option changes on every component card
    container.querySelectorAll('.bundle-item').forEach(function (card) {
      card.addEventListener('change', function (e) {
        var radio = e.target;
        if (!radio.matches('input[type="radio"][data-bundle-product]')) return;
        updateColorLabel(card, parseInt(radio.dataset.optionIndex, 10), radio.value);
        updateCard(card);
      });
    });

    setupFormIntercept(container);
  }

  /* ------------------------------------------------------------------ */
  /* Sync main-variant when the bundle product's own variant changes     */
  /* ------------------------------------------------------------------ */

  function syncMainVariant() {
    // custom-async.js fires "productVariants" after PDP variant changes;
    // pick up the new id from .f8pr-variant-selection[data-current-variant]
    var variantSelEl = document.querySelector('.f8pr-variant-selection');
    if (!variantSelEl) return;
    var newId = variantSelEl.dataset.currentVariant;
    if (!newId) return;
    document.querySelectorAll('.bundle-builder').forEach(function (c) {
      c.dataset.mainVariant = newId;
    });
  }

  /* ------------------------------------------------------------------ */
  /* Boot                                                                 */
  /* ------------------------------------------------------------------ */

  function boot() {
    document.querySelectorAll('.bundle-builder').forEach(initBundleBuilder);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.addEventListener('shopify:section:load', boot);
  window.addEventListener('productVariants', syncMainVariant);
})();
